import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileUp, Users, CheckCircle, AlertCircle, Download } from 'lucide-react';
import Papa from 'papaparse';
import api from '../../services/api';

const Import = () => {
  const navigate = useNavigate();
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [step, setStep] = useState(1); // 1: Select profile, 2: Upload file, 3: Map fields, 4: Confirm & Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Import profiles definition
  const importProfiles = [
    {
      id: 'students',
      name: 'Students',
      description: 'Import student information including contact and emergency details',
      icon: Users,
      fields: [
        { key: 'first_name', label: 'First Name', required: true },
        { key: 'last_name', label: 'Last Name', required: true },
        { key: 'gender', label: 'Gender', required: false },
        { key: 'age', label: 'Age', required: false },
        { key: 'experience', label: 'Experience', required: false },
        { key: 'parent_name', label: 'Parent Name', required: false },
        { key: 'parent_email', label: 'Parent Email', required: false },
        { key: 'parent_phone', label: 'Parent Phone', required: false },
        { key: 'emergency_contact_name', label: 'Emergency Contact Name', required: false }
      ]
    }
  ];

  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
    setStep(2);
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setFile(uploadedFile);

    // Parse CSV file
    Papa.parse(uploadedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvData(results.data);
        setCsvHeaders(results.meta.fields || []);

        // Auto-map fields with matching names
        const autoMapping = {};
        selectedProfile.fields.forEach(field => {
          const matchingHeader = results.meta.fields.find(header =>
            header.toLowerCase().replace(/[^a-z0-9]/g, '_') === field.key.toLowerCase()
          );
          if (matchingHeader) {
            autoMapping[field.key] = matchingHeader;
          }
        });
        setFieldMapping(autoMapping);
        setStep(3);
      },
      error: (error) => {
        console.error('Error parsing CSV:', error);
        alert('Error parsing CSV file. Please check the file format.');
      }
    });
  };

  const handleMappingChange = (fieldKey, csvHeader) => {
    setFieldMapping(prev => ({
      ...prev,
      [fieldKey]: csvHeader
    }));
  };

  const canProceedToImport = () => {
    // Check if all required fields are mapped
    const requiredFields = selectedProfile.fields.filter(f => f.required);
    return requiredFields.every(field => fieldMapping[field.key]);
  };

  const handleImport = async () => {
    setImporting(true);
    setImportResult(null);

    try {
      // Transform CSV data according to field mapping
      const transformedData = csvData.map(row => {
        const transformedRow = {};
        Object.keys(fieldMapping).forEach(fieldKey => {
          const csvHeader = fieldMapping[fieldKey];
          if (csvHeader && row[csvHeader] !== undefined) {
            transformedRow[fieldKey] = row[csvHeader];
          }
        });
        return transformedRow;
      });

      // Send to backend
      const response = await api.request(`/import/${selectedProfile.id}`, {
        method: 'POST',
        body: {
          data: transformedData
        }
      });

      setImportResult({
        success: true,
        message: response.message || 'Import completed successfully',
        imported: response.imported || 0,
        failed: response.failed || 0,
        errors: response.errors || []
      });
      setStep(4);
    } catch (error) {
      console.error('Import error:', error);
      setImportResult({
        success: false,
        message: error.message || 'Import failed',
        errors: [error.message]
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleCSV = () => {
    if (!selectedProfile) return;

    const headers = selectedProfile.fields.map(f => f.label).join(',');
    const sampleRow = selectedProfile.fields.map(f => {
      if (f.key === 'first_name') return 'John';
      if (f.key === 'last_name') return 'Doe';
      if (f.key === 'gender') return 'Male';
      if (f.key === 'age') return '12';
      if (f.key === 'experience') return 'Beginner';
      if (f.key === 'parent_name') return 'Jane Doe';
      if (f.key === 'parent_email') return 'jane@example.com';
      if (f.key === 'parent_phone') return '555-1234';
      if (f.key === 'emergency_contact_name') return 'Jim Doe';
      return '';
    }).join(',');

    const csv = `${headers}\n${sampleRow}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedProfile.id}_sample.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetImport = () => {
    setSelectedProfile(null);
    setFile(null);
    setCsvData(null);
    setCsvHeaders([]);
    setFieldMapping({});
    setStep(1);
    setImporting(false);
    setImportResult(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/settings/tools')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <FileUp className="w-8 h-8 text-[#7A99D8]" />
          <div>
            <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Import Data</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {step === 1 && 'Select an import profile'}
              {step === 2 && `Upload ${selectedProfile?.name} CSV file`}
              {step === 3 && 'Map CSV columns to fields'}
              {step === 4 && 'Import complete'}
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-4xl mx-auto">
        {/* Step 1: Select Profile */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Choose Import Type
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {importProfiles.map((profile) => {
                const Icon = profile.icon;
                return (
                  <button
                    key={profile.id}
                    onClick={() => handleProfileSelect(profile)}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:border-[#7A99D8] dark:hover:border-[#7A99D8] transition-colors text-left group"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-[#7A99D8] transition-colors">
                          {profile.name}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {profile.description}
                        </p>
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 dark:text-gray-500 font-medium mb-2">Fields:</p>
                          <div className="flex flex-wrap gap-2">
                            {profile.fields.map(field => (
                              <span
                                key={field.key}
                                className={`text-xs px-2 py-1 rounded ${
                                  field.required
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {field.label}{field.required && ' *'}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Upload File */}
        {step === 2 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Upload {selectedProfile.name} CSV File
            </h2>

            <div className="mb-6">
              <button
                onClick={downloadSampleCSV}
                className="inline-flex items-center gap-2 text-sm text-[#7A99D8] hover:text-[#6A89C8] font-medium"
              >
                <Download className="w-4 h-4" />
                Download Sample CSV Template
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Drag and drop your CSV file here, or click to browse
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="inline-flex items-center px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8] cursor-pointer"
              >
                <FileUp className="w-4 h-4 mr-2" />
                Choose File
              </label>
            </div>

            <div className="mt-6">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Map Fields */}
        {step === 3 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Map CSV Columns to Fields
            </h2>

            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Found {csvData.length} rows in your CSV file. Map the columns from your CSV to the {selectedProfile.name.toLowerCase()} fields below.
              </p>
            </div>

            {/* Side by Side Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Left Side: Field Mapping */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                  Field Mapping
                </h3>
                <div className="space-y-4">
                  {selectedProfile.fields.map(field => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <select
                        value={fieldMapping[field.key] || ''}
                        onChange={(e) => handleMappingChange(field.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">-- Select CSV Column --</option>
                        {csvHeaders.map(header => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side: Data Preview */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                  Data Preview (First 10 rows)
                </h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                        <tr>
                          {csvHeaders.map((header, index) => (
                            <th
                              key={index}
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {csvData.slice(0, 10).map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            {csvHeaders.map((header, colIndex) => (
                              <td
                                key={colIndex}
                                className="px-3 py-2 text-xs text-gray-900 dark:text-gray-100 whitespace-nowrap"
                              >
                                {row[header] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {csvData.length > 10 && (
                    <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2 text-xs text-gray-600 dark:text-gray-400 text-center border-t border-gray-200 dark:border-gray-700">
                      Showing 10 of {csvData.length} rows
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={!canProceedToImport() || importing}
                className="flex-1 px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8] disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Begin Import
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 4 && importResult && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-center mb-6">
              {importResult.success ? (
                <>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Import Completed
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {importResult.message}
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Import Failed
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {importResult.message}
                  </p>
                </>
              )}
            </div>

            {importResult.success && (
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Successfully Imported</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {importResult.imported}
                  </p>
                </div>
                {importResult.failed > 0 && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Failed</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {importResult.failed}
                    </p>
                  </div>
                )}
              </div>
            )}

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Errors:</p>
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                  {importResult.errors.map((error, idx) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={resetImport}
                className="flex-1 px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8]"
              >
                Import More Data
              </button>
              <button
                onClick={() => navigate('/students')}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                View Students
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Import;
